// ICP Engine — konfigurowalne pozyskiwanie B2B (T52 2026-06-01)
// Definiujesz KIM jesteś i KOGO szukasz → LLM generuje zapytania → discovery (Serper)
// → leady do b2b_leads → scrape kontaktów. Generalizuje MASKILa poza LED/wnętrza.
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Target, Sparkles, Search, Globe, Mail, Phone, ExternalLink,
  Loader2, CheckCircle2, Users, RefreshCw,
} from 'lucide-react';
import {
  icpGetSegments, icpGet, icpSave, icpGenerateQueries,
  icpDiscover, icpExtractContacts, icpListLeads,
} from '../lib/api';

const PAGE_SIZE = 60;

export default function IcpEngine({ lang }) {
  const [catalog, setCatalog] = useState({ segments: [], countries: [] });
  const [icp, setIcp] = useState(null);
  const [loading, setLoading] = useState(true);

  // form
  const [business, setBusiness] = useState('');
  const [products, setProducts] = useState('');     // comma-separated
  const [segments, setSegments] = useState([]);      // [keys]
  const [countries, setCountries] = useState(['PL']);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // discovery
  const [discSegment, setDiscSegment] = useState('');
  const [discCountry, setDiscCountry] = useState('PL');
  const [discPages, setDiscPages] = useState(3);
  const [discovering, setDiscovering] = useState(false);
  const [extracting, setExtracting] = useState(false);

  // leads
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [fSegment, setFSegment] = useState('all');
  const [fCountry, setFCountry] = useState('all');
  const [fQuery, setFQuery] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, cur] = await Promise.all([icpGetSegments(), icpGet()]);
      setCatalog(cat);
      if (cur.configured) {
        setIcp(cur);
        setBusiness(cur.business || '');
        setProducts((cur.products || []).join(', '));
        setSegments(cur.segments || []);
        setCountries(cur.countries || ['PL']);
        setDiscSegment((cur.segments || [])[0] || '');
        setDiscCountry((cur.countries || ['PL'])[0] || 'PL');
      }
    } catch (e) {
      toast.error('Błąd ładowania ICP: ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }, []);

  const loadLeads = useCallback(async () => {
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (fSegment !== 'all') params.segment = fSegment;
      if (fCountry !== 'all') params.country = fCountry;
      if (fQuery.trim()) params.q = fQuery.trim();
      const r = await icpListLeads(params);
      setLeads(r.items || []);
      setLeadsTotal(r.total || 0);
    } catch (e) { /* silent */ }
  }, [page, fSegment, fCountry, fQuery]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { setPage(1); }, [fSegment, fCountry, fQuery]);

  const toggleSegment = (k) =>
    setSegments(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  const toggleCountry = (c) =>
    setCountries(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]);

  async function save() {
    if (business.trim().length < 5) { toast.error('Opisz krótko swój biznes (min. 5 znaków)'); return; }
    if (segments.length === 0) { toast.error('Wybierz min. 1 segment odbiorców'); return; }
    setSaving(true);
    try {
      await icpSave({
        business: business.trim(),
        products: products.split(',').map(p => p.trim()).filter(Boolean),
        segments, countries: countries.length ? countries : ['PL'],
      });
      toast.success('Profil ICP zapisany');
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd zapisu'); }
    setSaving(false);
  }

  async function generate() {
    setGenerating(true);
    try {
      const r = await icpGenerateQueries();
      toast.success(`Wygenerowano ${r.total_queries || 0} zapytań (${r.combos || 0} kombinacji segment×kraj)`);
      await loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd generowania (LLM)'); }
    setGenerating(false);
  }

  async function runDiscovery() {
    if (!discSegment) { toast.error('Wybierz segment'); return; }
    setDiscovering(true);
    try {
      toast.info(`Szukam: ${segLabel(discSegment)} / ${discCountry}…`);
      const r = await icpDiscover(discSegment, discCountry, discPages);
      toast.success(`Znaleziono ${r.discovered || 0}, dodano ${r.inserted || 0} nowych (${r.queries_run || 0} zapytań)`);
      loadLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd discovery'); }
    setDiscovering(false);
  }

  async function extract() {
    setExtracting(true);
    try {
      toast.info('Wyciągam kontakty ze stron www…');
      const r = await icpExtractContacts(50);
      toast.success(`Przeskanowano ${r.scanned}, email +${r.email}, telefon +${r.phone}`);
      loadLeads();
    } catch (e) { toast.error(e.response?.data?.detail || 'Błąd'); }
    setExtracting(false);
  }

  const segLabel = (k) => (catalog.segments.find(s => s.key === k) || {}).label || k;

  if (loading) {
    return <div className="space-y-4 max-w-[1100px]"><Skeleton className="h-40" /><Skeleton className="h-64" /></div>;
  }

  const totalPages = Math.ceil(leadsTotal / PAGE_SIZE) || 1;

  return (
    <div className="space-y-5 max-w-[1100px]">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
          <Target className="w-6 h-6 text-primary" /> Silnik pozyskiwania (ICP)
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Zdefiniuj <b>kim jesteś</b> i <b>kogo szukasz</b>. MASKIL sam ułoży zapytania (AI),
          przeszuka internet i zbierze odbiorców z kontaktami — w wybranych krajach.
        </p>
      </div>

      {/* 1. PROFIL ICP */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs">1</span>
            Twój profil
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">Czym się zajmujesz? (1-2 zdania)</Label>
            <Textarea value={business} onChange={e => setBusiness(e.target.value)}
              placeholder="np. Producent krzeseł i mebli tapicerowanych dla gastronomii i biur."
              className="mt-1" rows={2} />
          </div>
          <div>
            <Label className="text-xs">Produkty / kategorie (po przecinku)</Label>
            <Input value={products} onChange={e => setProducts(e.target.value)}
              placeholder="krzesła, fotele biurowe, meble do restauracji" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Kogo szukasz? (segmenty odbiorców)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {catalog.segments.map(s => (
                <button key={s.key} type="button" onClick={() => toggleSegment(s.key)}
                  className={`text-left p-2.5 rounded-lg border transition-colors ${
                    segments.includes(s.key)
                      ? 'border-primary/60 bg-primary/10'
                      : 'border-border hover:border-primary/30'}`}>
                  <div className="flex items-center gap-2">
                    {segments.includes(s.key)
                      ? <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                      : <span className="w-4 h-4 rounded border border-muted-foreground/40 flex-shrink-0" />}
                    <span className="text-sm font-medium">{s.label}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 ml-6">{s.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5" /> Kraje wyszukiwania
            </Label>
            <div className="flex flex-wrap gap-2">
              {catalog.countries.map(c => (
                <button key={c} type="button" onClick={() => toggleCountry(c)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                    countries.includes(c)
                      ? 'border-primary/60 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:border-primary/30'}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Zapisz profil
            </Button>
            {icp?.configured && (
              <span className="text-xs text-muted-foreground">
                Zapisano · {icp.segments?.length} segm. · {icp.countries?.join(', ')}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 2. GENEROWANIE ZAPYTAŃ (LLM) */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-fuchsia-500/20 text-fuchsia-300 flex items-center justify-center text-xs">2</span>
            Zapytania (AI)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            AI ułoży frazy wyszukiwania dla każdego segmentu × kraju na podstawie Twojego profilu.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" onClick={generate} disabled={generating || !icp?.configured} className="gap-1.5">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Generuj zapytania
            </Button>
            {icp?.has_queries && (
              <span className="text-xs text-emerald-300 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {icp.total_queries} fraz · {icp.combos} kombinacji
              </span>
            )}
            {!icp?.configured && <span className="text-xs text-muted-foreground">Najpierw zapisz profil (krok 1).</span>}
          </div>
        </CardContent>
      </Card>

      {/* 3. DISCOVERY */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-300 flex items-center justify-center text-xs">3</span>
            Szukaj odbiorców
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="min-w-[200px]">
              <Label className="text-xs">Segment</Label>
              <Select value={discSegment} onValueChange={setDiscSegment}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="wybierz" /></SelectTrigger>
                <SelectContent>
                  {(icp?.segments || []).map(k => <SelectItem key={k} value={k}>{segLabel(k)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[120px]">
              <Label className="text-xs">Kraj</Label>
              <Select value={discCountry} onValueChange={setDiscCountry}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(icp?.countries || ['PL']).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[110px]">
              <Label className="text-xs">Głębokość</Label>
              <Select value={String(discPages)} onValueChange={(v) => setDiscPages(Number(v))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5, 8, 10].map(n => <SelectItem key={n} value={String(n)}>{n} stron</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={runDiscovery} disabled={discovering || !icp?.has_queries} className="gap-1.5">
              {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Szukaj
            </Button>
            <Button variant="outline" onClick={extract} disabled={extracting} className="gap-1.5">
              {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
              Wyciągnij kontakty
            </Button>
          </div>
          {!icp?.has_queries && <p className="text-xs text-muted-foreground">Najpierw wygeneruj zapytania (krok 2).</p>}
        </CardContent>
      </Card>

      {/* 4. LEADY */}
      <Card className="glass-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" /> Znalezieni odbiorcy
              <Badge variant="outline" className="text-[10px]">{leadsTotal}</Badge>
            </span>
            <Button variant="ghost" size="sm" onClick={loadLeads}><RefreshCw className="w-3.5 h-3.5" /></Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Input value={fQuery} onChange={e => setFQuery(e.target.value)}
              placeholder="Szukaj: nazwa / strona / email" className="flex-1 min-w-[180px]" />
            <Select value={fSegment} onValueChange={setFSegment}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie segmenty</SelectItem>
                {catalog.segments.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fCountry} onValueChange={setFCountry}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Kraje</SelectItem>
                {catalog.countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {leads.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm">
              <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
              Brak leadów. Zdefiniuj profil → wygeneruj zapytania → kliknij „Szukaj".
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              {leads.map(l => (
                <div key={l.id} className="glass-card lift-hover rounded-lg p-3 border border-border">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm flex-1 min-w-0 truncate">{l.name || l.website}</div>
                    {l.segment_label && <Badge variant="outline" className="text-[9px] flex-shrink-0">{l.segment_label}</Badge>}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mt-1.5 text-xs text-muted-foreground">
                    {l.country && <span>🌍 {l.country}</span>}
                    {l.website && (
                      <a href={l.website} target="_blank" rel="noreferrer"
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        <ExternalLink className="w-3 h-3" /> strona
                      </a>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap mt-1.5 text-xs">
                    {l.email
                      ? <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-emerald-300"><Mail className="w-3 h-3" />{l.email}</a>
                      : <span className="text-muted-foreground/50 inline-flex items-center gap-1"><Mail className="w-3 h-3" />—</span>}
                    {l.phone
                      ? <span className="inline-flex items-center gap-1 text-blue-300"><Phone className="w-3 h-3" />{l.phone}</span>
                      : <span className="text-muted-foreground/50 inline-flex items-center gap-1"><Phone className="w-3 h-3" />—</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {leadsTotal > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Poprzednia</Button>
              <span className="text-sm text-muted-foreground">Strona {page} z {totalPages} · {leadsTotal} odbiorców</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Następna →</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
